'use client';

import dayjs from 'dayjs';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { useAppContext } from 'src/context/app';
import { useAuthContext } from 'src/auth/hooks';

import { tillAttGöraPost, STANDARDINSTÄLLNINGAR } from './AttGoraTyper';
import {
  skriv,
  TOM_LISTA,
  läsLista,
  NYCKEL_KLARA,
  läsInställningar,
  NYCKEL_INSTÄLLNINGAR,
} from './lagring';
import { bedömArbetsgivare } from './arbetsgivarprofil';
import { toDayjs, beräknaMyndighetsuppgifter } from './myndighetsdatum';
import { hämtaAvklarat, INGET_AVKLARAT } from './AttGoraAvklarat';
import { HämtaAllaUnderlag } from '../inkop/InkopService';
import { HämtaLöntagare } from '../loner/LoneService';
import { hämtaAllaKundfakturorInklBokförda } from '../bokforing/affarshandelser/skapa/kundfaktura/KundfakturaService';

import type { Avklarat } from './AttGoraAvklarat';
import type { UppgiftsTyp, Deklarationsprofil } from './myndighetsdatum';
import type { AttGöraPost, AttGöraInställningar } from './AttGoraTyper';

// ----------------------------------------------------------------------

/** Måste matcha regelmotorns standardfönster i `beräknaMyndighetsuppgifter`. */
const HISTORIK_DAGAR = 120;
const HORISONT_DAGAR = 400;

/**
 * Samlar allt organisationen behöver göra gentemot myndigheter och motparter.
 *
 * Myndighetsdatumen räknas ut lokalt av regelmotorn i `myndighetsdatum.ts`.
 * Övriga källor läggs till här; varje källa mappas till en `AttGöraPost`.
 */
export function useAttGöra() {
  // Myndighetsdatumen och de lokala avbockningarna hör till valet som sådant —
  // Privatperson har egna deklarationsdatum och egna inställningar, och är
  // därför `valdOrganisation`. Hämtningarna längre ned gäller en riktig
  // förening och gatear på `föreningsId`, som är undefined för Privatperson.
  const { organisation: förening, valdOrganisation: organisation } = useAppContext();
  const orgId = organisation?.id;
  const föreningsId = förening?.id;

  /**
   * När kontot skapades, enligt inloggningen. Myndighetsuppgifter som förföll
   * dessförinnan hör inte hemma i listan — en ny användare ska inte mötas av
   * röda påminnelser om tidpunkter som passerade innan hen fanns i appen.
   */
  const { user } = useAuthContext();
  const iAppenSedan = useMemo(
    () => toDayjs(user?.metadata?.creationTime) ?? undefined,
    [user?.metadata?.creationTime]
  );

  /**
   * Allt organisationsberoende state hålls ihop med den orgId det lästes för.
   * Utan den bindningen visas förra organisationens avbockningar och
   * inställningar under renderingarna innan effekten hunnit läsa om — och
   * uppgifts-id:n som `agd-2026-07` är inte organisationsunika.
   */
  const [lagrat, setLagrat] = useState<{
    orgId?: string;
    inställningar: AttGöraInställningar;
    klara: string[];
  }>({ inställningar: STANDARDINSTÄLLNINGAR, klara: [] });

  const gällerAktuellOrg = lagrat.orgId === orgId;
  const inställningar = gällerAktuellOrg ? lagrat.inställningar : STANDARDINSTÄLLNINGAR;
  const klara = gällerAktuellOrg ? lagrat.klara : TOM_LISTA;

  const [fakturaposter, setFakturaposter] = useState<AttGöraPost[]>([]);
  /**
   * Organisationens egna uppgifter — avbockningar och antal löntagare — bärs
   * med sitt orgId av samma skäl som `lagrat` ovan. Utan bindningen lever förra
   * organisationens svar kvar tills det nya kommit in, och arbetsgivar-
   * bedömningen skulle då kunna slå om till fel håll i ett par renderingar.
   */
  const [hämtat, setHämtat] = useState<{
    orgId?: string;
    avklarat: Avklarat;
    antalLöntagare?: number;
  }>({ avklarat: INGET_AVKLARAT });

  const gällerHämtat = hämtat.orgId === orgId;
  const avklarat = gällerHämtat ? hämtat.avklarat : INGET_AVKLARAT;
  const antalLöntagare = gällerHämtat ? hämtat.antalLöntagare : undefined;

  const [fakturaAvklarat, setFakturaAvklarat] = useState<Record<string, string>>({});
  /** Bumpas för att tvinga omhämtning, t.ex. när panelen öppnas. */
  const [omhämtning, setOmhämtning] = useState(0);
  const uppdatera = useCallback(() => setOmhämtning((n) => n + 1), []);

  // En ny dag ska ändra "3 dagar kvar" utan omladdning.
  const [idag, setIdag] = useState(() => dayjs().startOf('day'));
  useEffect(() => {
    const timer = setInterval(() => {
      setIdag((tidigare) => {
        const nu = dayjs().startOf('day');
        return nu.isSame(tidigare, 'day') ? tidigare : nu;
      });
    }, 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!orgId) {
      setLagrat({ orgId: undefined, inställningar: STANDARDINSTÄLLNINGAR, klara: TOM_LISTA });
      return;
    }
    setLagrat({
      orgId,
      inställningar: läsInställningar(`${NYCKEL_INSTÄLLNINGAR}:${orgId}`),
      klara: läsLista(`${NYCKEL_KLARA}:${orgId}`),
    });
  }, [orgId]);

  /** Skriver bara när state redan tillhör den aktuella organisationen. */
  const ändraInställningar = useCallback(
    (beräkna: (tidigare: AttGöraInställningar) => AttGöraInställningar) => {
      if (!orgId) return;
      setLagrat((tidigare) => {
        const bas = tidigare.orgId === orgId ? tidigare.inställningar : STANDARDINSTÄLLNINGAR;
        const nya = beräkna(bas);
        skriv(`${NYCKEL_INSTÄLLNINGAR}:${orgId}`, nya);
        return { orgId, inställningar: nya, klara: tidigare.orgId === orgId ? tidigare.klara : TOM_LISTA };
      });
    },
    [orgId]
  );

  const uppdateraInställningar = useCallback(
    (ändring: Partial<AttGöraInställningar>) =>
      ändraInställningar((tidigare) => ({ ...tidigare, ...ändring })),
    [ändraInställningar]
  );

  const växlaTyp = useCallback(
    (typ: UppgiftsTyp) =>
      ändraInställningar((tidigare) => ({
        ...tidigare,
        dolda: tidigare.dolda.includes(typ)
          ? tidigare.dolda.filter((t) => t !== typ)
          : [...tidigare.dolda, typ],
      })),
    [ändraInställningar]
  );

  const växlaKlar = useCallback(
    (id: string) => {
      if (!orgId) return;
      setLagrat((tidigare) => {
        const bas = tidigare.orgId === orgId ? tidigare.klara : TOM_LISTA;
        const nya = bas.includes(id) ? bas.filter((k) => k !== id) : [...bas, id];
        skriv(`${NYCKEL_KLARA}:${orgId}`, nya);
        return {
          orgId,
          inställningar: tidigare.orgId === orgId ? tidigare.inställningar : STANDARDINSTÄLLNINGAR,
          klara: nya,
        };
      });
    },
    [orgId]
  );

  // Fakturor att agera på. Två källor, båda sekundära: ett fel här får aldrig
  // hindra myndighetsdatumen från att visas.
  useEffect(() => {
    let avbruten = false;
    if (!föreningsId) {
      setFakturaposter([]);
      setFakturaAvklarat({});
      return undefined;
    }

    /** Obetalda kundfakturor — en påminnelse ska skickas. */
    const kundfakturor = hämtaAllaKundfakturorInklBokförda(föreningsId).then((svar) =>
      ((Array.isArray(svar) ? svar : []) as any[])
        .filter((f) => !f?.matchadBetalning)
        .map((f, index): AttGöraPost | null => {
          const förfallodag = toDayjs(f?.förfalloDag);
          if (!förfallodag) return null;
          return {
            id: `kundfaktura-${f?.fakturaReferens ?? index}-${förfallodag.format('YYYYMMDD')}`,
            titel: `Obetald kundfaktura ${f?.fakturaReferens ?? ''}`.trim(),
            detalj: f?.kund?.namn ? `Kund: ${f.kund.namn}` : 'Skicka påminnelse.',
            källa: 'FAKTURA',
            avsändare: 'Kundfaktura',
            förfallodag,
            belopp: typeof f?.beloppInklMoms === 'number' ? f.beloppInklMoms : undefined,
          };
        })
        .filter((p): p is AttGöraPost => p !== null)
    );

    /**
     * Leverantörsfakturor. Ett bokfört underlag betyder att betalningen är matchad
     * och slutförd, alltså att fakturan är betald — den posten avklaras automatiskt
     * i stället för att tyst försvinna ur listan. Förfallodagen kommer ur
     * AI-tolkningen; fakturor utan förfallodag hoppas över hellre än att gissas.
     */
    const leverantörsfakturor = HämtaAllaUnderlag(föreningsId).then((svar) => {
      const poster: AttGöraPost[] = [];
      const betalda: Record<string, string> = {};

      ((Array.isArray(svar) ? svar : []) as any[]).forEach((underlag, index) => {
        const info = underlag?.inköpTolkning?.fakturainformationTolkning;
        const förfallodag = toDayjs(info?.förfalloDag);
        if (!förfallodag) return;

        const id = `levfaktura-${underlag?.id ?? index}`;
        const leverantör = info?.leverantörsnamn ?? 'okänd leverantör';
        poster.push({
          id,
          titel: `Betala leverantörsfaktura – ${leverantör}`,
          detalj: info?.fakturanummer ? `Fakturanummer ${info.fakturanummer}` : undefined,
          källa: 'FAKTURA',
          avsändare: leverantör,
          förfallodag,
          belopp: typeof info?.totaltBelopp === 'number' ? info.totaltBelopp : undefined,
        });
        if (underlag?.bokförd) betalda[id] = 'Betald';
      });

      return { poster, betalda };
    });

    Promise.allSettled([kundfakturor, leverantörsfakturor]).then((resultat) => {
      if (avbruten) return;
      const kundposter = resultat[0].status === 'fulfilled' ? resultat[0].value : [];
      const lev = resultat[1].status === 'fulfilled' ? resultat[1].value : { poster: [], betalda: {} };
      setFakturaposter([...kundposter, ...lev.poster]);
      setFakturaAvklarat(lev.betalda);
    });

    return () => {
      avbruten = true;
    };
  }, [föreningsId, omhämtning]);

  /**
   * Vilka deklarationer som redan är bokförda/inlämnade, och hur många
   * löntagare organisationen har. Löntagarna används bara för att avgöra om
   * organisationen alls är arbetsgivare — en hämtning som fallerar lämnar
   * antalet okänt i stället för att räknas som noll.
   */
  useEffect(() => {
    let avbruten = false;
    // Ingen återställning behövs: `gällerHämtat` gör redan gammal data osynlig
    // så snart orgId inte längre matchar.
    if (!föreningsId) return undefined;

    Promise.allSettled([hämtaAvklarat(föreningsId), HämtaLöntagare(föreningsId)]).then(
      ([avklaratSvar, löntagarSvar]) => {
        if (avbruten) return;
        setHämtat({
          orgId,
          avklarat: avklaratSvar.status === 'fulfilled' ? avklaratSvar.value : INGET_AVKLARAT,
          antalLöntagare:
            löntagarSvar.status === 'fulfilled' && Array.isArray(löntagarSvar.value)
              ? löntagarSvar.value.length
              : undefined,
        });
      }
    );

    return () => {
      avbruten = true;
    };
  }, [föreningsId, orgId, omhämtning]);

  /** Lokalt val vinner över organisationsregistret; 'INGEN' stänger av momsdatumen. */
  const momsperiod = useMemo(() => {
    const val = inställningar.momsperiod ?? organisation?.momsDeklarationsPeriod;
    return val === 'INGEN' || !val ? undefined : val;
  }, [inställningar.momsperiod, organisation?.momsDeklarationsPeriod]);

  /**
   * Arbetsgivardeklarationer hör bara hemma hos den som faktiskt är arbetsgivare.
   * Bedömningen görs ur organisationens egna uppgifter; användarens uttryckliga
   * val vinner över den, precis som för momsperioden.
   */
  const arbetsgivarbedömning = useMemo(
    () =>
      bedömArbetsgivare({
        organisationsTyp: organisation?.organisationsTyp,
        antalLöntagare,
        harArbetsgivardeklarationer: avklarat.harArbetsgivardeklarationer,
      }),
    [organisation?.organisationsTyp, antalLöntagare, avklarat.harArbetsgivardeklarationer]
  );

  const ärArbetsgivare = inställningar.ärArbetsgivareVal ?? arbetsgivarbedömning.ärArbetsgivare;

  const profil = useMemo<Deklarationsprofil>(
    () => ({
      organisationsTyp: organisation?.organisationsTyp,
      räkenskapsår: organisation?.räkenskapsår,
      momsDeklarationsPeriod: momsperiod,
      ärArbetsgivare: organisation?.organisationsTyp === 'PRIVATPERSON' ? false : ärArbetsgivare,
      harDebiteradPreliminärskatt: inställningar.harDebiteradPreliminärskatt,
      lämnarDigitalt: inställningar.lämnarDigitalt,
      iAppenSedan,
    }),
    [organisation, inställningar, momsperiod, ärArbetsgivare, iAppenSedan]
  );

  const poster = useMemo(() => {
    const myndighetsposter = beräknaMyndighetsuppgifter(profil, { idag })
      .filter((u) => !inställningar.dolda.includes(u.typ))
      .map(tillAttGöraPost);

    // Samma fönster som regelmotorn använder, så att betalda fakturor rullar av
    // listan i stället för att samlas på hög.
    const tidigast = idag.subtract(HISTORIK_DAGAR, 'day');
    const senast = idag.add(HORISONT_DAGAR, 'day');
    const fakturorIFönstret = fakturaposter.filter(
      (p) => !p.förfallodag.isBefore(tidigast, 'day') && !p.förfallodag.isAfter(senast, 'day')
    );

    return [...myndighetsposter, ...fakturorIFönstret].sort(
      (a, b) => a.förfallodag.valueOf() - b.förfallodag.valueOf()
    );
  }, [profil, fakturaposter, inställningar.dolda, idag]);

  /**
   * En uppgift är klar antingen för att den bevisligen är utförd (bokförd
   * arbetsgivardeklaration, inlämnad momsdeklaration) eller för att den bockats
   * av för hand. Den härledda vägen väger tyngst och går inte att kryssa bort.
   */
  const avklaratAutomatiskt = useMemo(
    () => ({ ...avklarat.anledningPerUppgift, ...fakturaAvklarat }),
    [avklarat, fakturaAvklarat]
  );

  const ärKlar = useCallback(
    (id: string) => Boolean(avklaratAutomatiskt[id]) || klara.includes(id),
    [avklaratAutomatiskt, klara]
  );

  const öppna = useMemo(() => poster.filter((p) => !ärKlar(p.id)), [poster, ärKlar]);

  const nästa = öppna[0];
  const antalFörsenade = öppna.filter((p) => p.förfallodag.isBefore(idag, 'day')).length;

  const ärMomsregistrerad = useMemo(() => {
    if (organisation?.organisationsTyp === 'PRIVATPERSON') return false;
    if (organisation?.momsregisteringsnummer) return true;
    if (organisation?.organisationsTyp === 'AKTIEBOLAG' || organisation?.organisationsTyp === 'FÖRETAG') return true;
    return false;
  }, [organisation?.organisationsTyp, organisation?.momsregisteringsnummer]);

  return {
    idag,
    poster,
    öppna,
    nästa,
    klara,
    ärKlar,
    avklaratAutomatiskt,
    antalFörsenade,
    inställningar,
    uppdatera,
    växlaKlar,
    växlaTyp,
    uppdateraInställningar,
    momsperiod,
    ärArbetsgivare,
    arbetsgivarbedömning,
    ärMomsregistrerad,
    ärPrivatperson: organisation?.organisationsTyp === 'PRIVATPERSON',
    harRäkenskapsår: (organisation?.räkenskapsår?.length ?? 0) > 0,
  };
}
